import {
  ICustomFieldRecordSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { parseISO } from 'date-fns/parseISO';
import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useCustomEventListener } from '../../../../../hooks/useCustomEvent';
import { FeatureFlag } from '../../../../../services/launchDarkly';
import {
  CUSTOMER_AUTOMATION_CustomField_getMirrorEventUpdates,
  QuantityAutomationProductsById,
  anaplan_coreSaasNeedsResave,
  fexa_calculateOnboardingUnitPrice,
} from '../../../../../types';

/**
 * Handle automation events for quote offerings
 * When a quote offeing is updated and has quantity automation enabled,
 * this handles updating the current quote item quantities based on other quote offering
 * updates.
 *
 * @param quoteOffering The quote offering to update
 * @param form The form state to update
 * @param onSubmit The submit function to call after updates
 */
export const useHandleQuoteOfferingAutomationEvent = (
  quoteOffering: Maybe<IQuoteOfferingRespSchema>,
  form: UseFormReturn<IQuoteOfferingReqSchema>,
  onSubmit: () => void,
) => {
  const { setValue, trigger } = form;

  useCustomEventListener('updateQuoteOfferingQty', (event) => {
    const { sourceOffering, scopedOfferingIds } = event;
    if (
      !quoteOffering ||
      // Skip if this offering was not specified as a target
      !scopedOfferingIds.has(quoteOffering.offeringId) ||
      // Skip if emitted from self
      sourceOffering.quoteOfferingId === quoteOffering.id ||
      // Skip if in the same quote offering chain
      (sourceOffering.parentQuoteOfferingId &&
        sourceOffering.parentQuoteOfferingId ===
          quoteOffering.parentQuoteOfferingId)
    ) {
      return;
    }

    // Exit if this line item does not overlap with the source offering
    const startDate = parseISO(quoteOffering.startDate);
    const endDate = parseISO(quoteOffering.endDate);
    if (
      isAfter(startDate, sourceOffering.endDate) ||
      isBefore(endDate, sourceOffering.startDate)
    ) {
      return;
    }

    let needsUpdate = false;
    quoteOffering.items.forEach((item, i) => {
      if (
        event.targetProductIds.has(item.productId) &&
        item.quantity !== event.quantity
      ) {
        needsUpdate = true;
        setValue(`items.${i}.quantity`, event.quantity);
      }
    });

    if (needsUpdate) {
      trigger().then((_isValid) => {
        if (_isValid) {
          // try to avoid concurrent updates
          setTimeout(() => {
            onSubmit();
            // handleSubmit((data) => onSubmit({ data }), onError)();
          }, Math.random() * 100);
        }
      });
    }
  });
};

/**
 * Fexa cross-offering automation.
 *
 * When the CMMS source quantity changes in another offering, recalculate the
 * derived onboarding custom price for any onboarding targets held by this
 * offering, then re-save. The save is registered with the event so the source
 * re-fetches the quote once, after this offering has saved.
 *
 * @param customerAutomationFexa Fexa feature flag config
 * @param quoteOffering The quote offering to update
 * @param form The form state to update
 * @param onSubmit Saves this offering without re-fetching the quote
 */
export const useHandleFexaAutomationEvent = (
  customerAutomationFexa: FeatureFlag['customerAutomationFexa'],
  quoteOffering: Maybe<IQuoteOfferingRespSchema>,
  form: UseFormReturn<IQuoteOfferingReqSchema>,
  onSubmit: () => Promise<void>,
) => {
  const { setValue, trigger, getValues } = form;

  useCustomEventListener('updateQuoteOfferingFexaCmms', (event) => {
    if (!customerAutomationFexa || !quoteOffering) {
      return;
    }
    const { sourceOffering, cmmsQuantity, waitUntil } = event;
    if (
      // Skip if emitted from self
      sourceOffering.quoteOfferingId === quoteOffering.id ||
      // Skip if in the same quote offering chain
      (sourceOffering.parentQuoteOfferingId &&
        sourceOffering.parentQuoteOfferingId ===
          quoteOffering.parentQuoteOfferingId)
    ) {
      return;
    }

    // Exit if this offering does not overlap with the source offering
    const startDate = parseISO(quoteOffering.startDate);
    const endDate = parseISO(quoteOffering.endDate);
    if (
      isAfter(startDate, sourceOffering.endDate) ||
      isBefore(endDate, sourceOffering.startDate)
    ) {
      return;
    }

    const { onboarding } = customerAutomationFexa;
    const onboardingTargetIds = new Set(onboarding?.targetProductIds ?? []);
    const formItems = getValues('items');

    let needsUpdate = false;
    quoteOffering.items.forEach((item) => {
      if (onboarding && onboardingTargetIds.has(item.productId)) {
        // Resolve by item id: the form is sorted by position, while
        // quoteOffering.items keeps server order, which can differ.
        const formIndex = formItems.findIndex(({ id }) => id === item.id);
        const unitAmount = fexa_calculateOnboardingUnitPrice(
          cmmsQuantity,
          onboarding,
        );
        if (formIndex !== -1 && item.customPrice?.amount !== unitAmount) {
          needsUpdate = true;
          setValue(`items.${formIndex}.customPrice.unitAmount`, unitAmount);
          if (item.quantity !== 1) {
            setValue(`items.${formIndex}.quantity`, 1);
          }
        }
      }
    });

    if (needsUpdate) {
      waitUntil(
        trigger().then((isValid) => {
          if (isValid) {
            return onSubmit();
          }
        }),
      );
    }
  });
};

/**
 * Custom-field mirror automation (cross-offering).
 *
 * When another offering holding mirror source lines is saved, set the quantity
 * of every quantity-locked line in this offering that shares a mirror match
 * key, then re-save so roll-ups and pricing follow.
 *
 * @param customerAutomationCustomFieldQuantity Feature flag config (custom field API names)
 * @param productsById Catalog products (with custom fields) keyed by product id
 * @param quoteOffering The quote offering to update
 * @param form The form state to update
 * @param onSubmit The submit function to call after updates
 */
export const useHandleCustomFieldMirrorAutomationEvent = (
  customerAutomationCustomFieldQuantity: FeatureFlag['customerAutomationCustomFieldQuantity'],
  productsById: QuantityAutomationProductsById,
  quoteOffering: Maybe<IQuoteOfferingRespSchema>,
  form: UseFormReturn<IQuoteOfferingReqSchema>,
  onSubmit: () => void,
) => {
  const { setValue, trigger, getValues } = form;

  useCustomEventListener('updateQuoteOfferingMirrorQty', (event) => {
    if (!customerAutomationCustomFieldQuantity || !quoteOffering) {
      return;
    }
    const { sourceOffering, quantityByMatchKey } = event;
    // Skip if emitted from self or from anywhere in the same quote offering
    // chain (parent or a scheduled-change child)
    const sourceChainRootId =
      sourceOffering.parentQuoteOfferingId ?? sourceOffering.quoteOfferingId;
    const chainRootId = quoteOffering.parentQuoteOfferingId ?? quoteOffering.id;
    if (sourceChainRootId === chainRootId) {
      return;
    }

    // Exit if this offering does not overlap with the source offering
    const startDate = parseISO(quoteOffering.startDate);
    const endDate = parseISO(quoteOffering.endDate);
    if (
      isAfter(startDate, sourceOffering.endDate) ||
      isBefore(endDate, sourceOffering.startDate)
    ) {
      return;
    }

    const formItems = getValues('items');
    const updates = CUSTOMER_AUTOMATION_CustomField_getMirrorEventUpdates({
      customerAutomationCustomFieldQuantity,
      productsById,
      quoteOffering,
      formItems,
      quantityByMatchKey,
    });
    if (!updates.length) {
      return;
    }

    // Resolve by item id: the form's own item order can differ from
    // quoteOffering.items (unsaved add/remove, display sorting).
    const formIndexById = formItems.reduce<Record<string, number>>(
      (acc, formItem, index) => {
        if (formItem.id) {
          acc[formItem.id] = index;
        }
        return acc;
      },
      {},
    );

    let didApplyUpdate = false;
    updates.forEach(({ id, quantity }) => {
      const index = formIndexById[id];
      if (index !== undefined) {
        didApplyUpdate = true;
        setValue(`items.${index}.quantity`, quantity);
      }
    });
    if (!didApplyUpdate) {
      return;
    }

    trigger().then((_isValid) => {
      if (_isValid) {
        // try to avoid concurrent updates
        setTimeout(() => {
          onSubmit();
        }, Math.random() * 100);
      }
    });
  });
};

/**
 * Anaplan: re-save the Core SaaS Platform line when a quote custom field that
 * drives its matrix price changes (`of_employees` edited directly, or
 * `modules_enabled` rewritten by an Apps save).
 *
 * This hook only decides WHETHER to re-save. The price itself is derived in
 * `onSubmit` by `CUSTOMER_AUTOMATION_Anaplan_CoreSaas`, which runs before the
 * dirty check. Setting the form value here instead would race the form reset
 * that follows every quote re-fetch (it rebuilds `customPrice` from the server
 * without re-applying it), after which the save would be skipped as unchanged.
 *
 * Nothing fires for the values present on first render, so a page load never
 * issues saves on its own; a stale saved price is corrected on the line's next
 * manual save.
 *
 * @param customerAutomationAnaplan Anaplan feature flag config
 * @param quoteOffering The saved quote offering this form edits
 * @param customFields The quote's current custom fields
 * @param isReadOnly Whether this line cannot be edited (read-only quote, locked
 *   Amendment V2 baseline line, or removed line)
 * @param onSubmit The submit function to call when a re-save is needed
 */
export const useHandleAnaplanCustomFieldChange = (
  customerAutomationAnaplan: FeatureFlag['customerAutomationAnaplan'],
  quoteOffering: Maybe<IQuoteOfferingRespSchema>,
  customFields: Maybe<ICustomFieldRecordSchema>,
  isReadOnly: boolean,
  onSubmit: () => void,
) => {
  // Only a change in the two driving values should fire the effect; everything
  // else is read through refs so the latest render's values are used.
  const latestRef = useRef({
    customerAutomationAnaplan,
    quoteOffering,
    customFields,
    isReadOnly,
    onSubmit,
  });
  latestRef.current = {
    customerAutomationAnaplan,
    quoteOffering,
    customFields,
    isReadOnly,
    onSubmit,
  };

  // Compared as strings: the optimistic custom-field merge and the quote
  // re-fetch that follows render the same values, and a NUMBER field may
  // arrive as a number from one and a string from the other.
  const drivingValues = [
    customerAutomationAnaplan?.employeesCustomFieldKey,
    customerAutomationAnaplan?.modulesCustomFieldKey,
  ]
    .map((key) => String((key && customFields?.[key]) ?? ''))
    .join('|');
  const previousDrivingValuesRef = useRef(drivingValues);

  useEffect(() => {
    if (previousDrivingValuesRef.current === drivingValues) {
      return;
    }
    previousDrivingValuesRef.current = drivingValues;

    const {
      customerAutomationAnaplan: config,
      quoteOffering: latestQuoteOffering,
      customFields: latestCustomFields,
      isReadOnly: latestIsReadOnly,
    } = latestRef.current;
    if (
      latestIsReadOnly ||
      !anaplan_coreSaasNeedsResave({
        customerAutomationAnaplan: config,
        quoteOffering: latestQuoteOffering,
        customFields: latestCustomFields,
      })
    ) {
      return;
    }

    // try to avoid concurrent updates
    const timeoutId = setTimeout(() => {
      latestRef.current.onSubmit();
    }, Math.random() * 100);
    return () => clearTimeout(timeoutId);
  }, [drivingValues]);
};
